import React, { useState } from 'react';
import { useQuery, useMutation, useApolloClient } from '@apollo/react-hooks';
import gql from 'graphql-tag';

import {
  Button,
  TextWithLinks,
  ErrorElement,
  Loader,
  URLInput,
  TokenInput,
  EmailInput,
  PasswordInput,
  SubDomainInput,
} from 'components';
import Icon from 'assets/icons';

import { Logger } from 'services';
import { refreshIntegrationsQuery, GetUserQuery } from 'services/apollo/queries';
import { LoadingStatusType } from 'helpers/constants';
import { ManageAppsIntegrationImage } from 'screens/ManageAppScreen';

import styles from './TokenIntegrationModalContent.module.scss';

interface TokenIntegrationModalContent {
  integration: any;
  /** @function - function to close the main modal */
  closeModal: any;
  theme?: string;
}

const addTokenIntegrationMutation = gql`
  mutation addTokenIntegration(
    $displayName: String!
    $fields: [UserIntegrationConfigField]
    $integrationUserId: String!
    $type: String!
  ) {
    createUserIntegration(
      displayName: $displayName
      fields: $fields
      integrationUserId: $integrationUserId
      type: $type
    ) {
      ok
      error
    }
  }
`;

export const TokenIntegrationModalContent: React.FC<TokenIntegrationModalContent> = ({
  integration,
  closeModal,
  theme = 'Default',
}) => {
  const client = useApolloClient();
  const [errorObject, setErrorObject] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [token, setToken] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [url, setUrl] = useState('');
  const [subdomain, setSubdomain] = useState('');

  const [addTokenIntegration] = useMutation(addTokenIntegrationMutation);

  const { error, data } = useQuery(GetUserQuery, {
    fetchPolicy: 'cache-and-network',
  });

  if (data === undefined) return <Loader />;

  if (error) return <Loader color="Red" />;

  const { me } = data;
  const [id] = integration.type!.split('@');

  const getCloseIcon = () => {
    if (theme === 'TwoTone') {
      return <Icon.Close className={styles.icon} />;
    } else {
      return (
        <>
          <Icon.ArrowLeft className={styles.icon} /> Back
        </>
      );
    }
  };

  const handleSubDomainChanged = (subdomain: any) => {
    const rootUrl = integration.userConfig.fields.find((x: any) => x.name === 'subDomain').label;

    setSubdomain(subdomain);
    setUrl(`https://${subdomain}${rootUrl}`);
  };

  const getIsButtonDisabled = () => {
    let isDisabled = false;
    isDisabled =
      (integration.userConfig.fields.find((x: any) => x.name === 'url') && !url) ||
      (integration.userConfig.fields.find((x: any) => x.name === 'token') && !token) ||
      (integration.userConfig.fields.find((x: any) => x.name === 'email') && !email) ||
      (integration.userConfig.fields.find((x: any) => x.name === 'password') && !password) ||
      (integration.userConfig.fields.find((x: any) => x.name === 'subDomain') && !subdomain);

    return isDisabled;
  };

  const validateFields = () => {
    let errors = {};

    integration.userConfig.fields.forEach((field: any) => {
      if (field.name === 'url') {
        errors = !url ? { ...errors, url: 'Url is required' } : errors;
      }

      if (field.name === 'token') {
        errors = !token ? { ...errors, token: 'Token is required' } : errors;
      }

      if (field.name === 'email') {
        if (!email) {
          errors = { ...errors, email: 'Email is required' };
        } else if (!/\S+@\S+\.\S+/.test(email)) {
          errors = { ...errors, email: 'Email is invalid' };
        }
      }

      if (field.name === 'password') {
        errors = !password ? { ...errors, password: 'Password is required' } : errors;
      }

      if (field.name === 'subDomain') {
        errors = !subdomain ? { ...errors, subdomain: 'Subdomain is required' } : errors;
      }
    });

    setErrorObject(errors);

    return !Object.keys(errors).length;
  };

  const generateFieldsObject = () => {
    let fields: any = [];

    integration.userConfig.fields.forEach((field: any) => {
      if (field.name === 'url') {
        fields.push({ name: field.name, value: url });
      }

      if (field.name === 'token') {
        fields.push({ name: field.name, value: token });
      }

      if (field.name === 'email') {
        fields.push({ name: field.name, value: email });
      }

      if (field.name === 'password') {
        fields.push({ name: field.name, value: password });
      }

      if (field.name === 'subDomain') {
        fields.push({ name: field.name, value: subdomain });
      }
    });

    return fields;
  };

  const handleSubmit = (e: any) => {
    e.preventDefault();

    if (!validateFields()) {
      return;
    }

    setSubmitting(true);

    addTokenIntegration({
      variables: {
        displayName: id,
        fields: generateFieldsObject(),
        integrationUserId: me.userProfile.id,
        type: integration.type,
      },
    }).then((data: any) => {
      const {
        data: {
          createUserIntegration: { error, ok },
        },
      } = data;

      if (error) {
        // TODO Add error logging
        Logger.log(error);
        setErrorObject({ general: LoadingStatusType.ERROR });
      }

      if (ok) {
        /* TODO - #32 Analytics
          tracking.trackEvent({
            event: eventTrackingNames.onboarding.connectApps.addApp,
            metadata: {
              [eventTrackingProperties.metadata.appType]: integration_type
            }
          });
         */

        client
          .query({
            query: refreshIntegrationsQuery,
            fetchPolicy: 'network-only',
          })
          .then(() => {
            closeModal();
          })
          .catch((error) => {
            Logger.error(error);
          });
      }

      setSubmitting(false);
    });
  };

  return (
    <>
      <div className={styles.back}>
        <Button className={styles.backBtn} type="Plain" onClick={closeModal}>
          {getCloseIcon()}
        </Button>
      </div>
      <div className={styles.tokenIntegration}>
        <div className={styles.upper}>
          <div className={styles.title}>
            <span className={styles.text}>Connect</span>
            <ManageAppsIntegrationImage itemIntegrationName={id} />
          </div>
          <div className={styles.description}>
            <strong>Instructions:</strong>
            <br />
            <TextWithLinks text={integration.userConfig.instructions} />
          </div>
        </div>

        <form className={styles.lower} onSubmit={handleSubmit}>
          <div className={styles.inputContainer}>
            {integration.userConfig.fields.map((field: any, key: number) => {
              if (field.name === 'url') {
                return (
                  <URLInput
                    key={key}
                    className={styles.input}
                    urlLabel={field.label}
                    url={url}
                    errors={errorObject}
                    handleUrlChanged={(e: any) => {
                      setUrl(e.target.value);
                    }}
                  />
                );
              } else if (field.name === 'token') {
                return (
                  <TokenInput
                    key={key}
                    className={styles.input}
                    tokenLabel={field.label}
                    token={token}
                    errors={errorObject}
                    handleTokenChanged={(e: any) => {
                      setToken(e.target.value);
                    }}
                  />
                );
              } else if (field.name === 'email') {
                return (
                  <EmailInput
                    key={key}
                    className={styles.input}
                    emailLabel={field.label}
                    email={email}
                    errors={errorObject}
                    handleEmailChanged={(e: any) => {
                      setEmail(e.target.value);
                    }}
                  />
                );
              } else if (field.name === 'password') {
                return (
                  <PasswordInput
                    key={key}
                    className={styles.input}
                    passwordLabel={field.label}
                    password={password}
                    errors={errorObject}
                    handlePasswordChanged={(e: any) => {
                      setPassword(e.target.value);
                    }}
                  />
                );
              } else if (field.name === 'subDomain') {
                return (
                  <SubDomainInput
                    key={key}
                    className={styles.input}
                    subDomainLabel={field.label}
                    subdomain={subdomain}
                    errors={errorObject}
                    handleSubDomainChanged={(e: any) => {
                      handleSubDomainChanged(e.target.value);
                    }}
                  />
                );
              } else {
                return null;
              }
            })}
          </div>
          <div className={styles.footer}>
            <div className={styles.statusMessage}>
              {
                // TODO
                //@ts-ignore
                errorObject!.general && <ErrorElement errors={errorObject} />
              }
            </div>
            <Button onClick={handleSubmit} disabled={getIsButtonDisabled() || submitting}>
              {submitting ? <Loader color="White" width={30} height={30} /> : 'Connect'}
            </Button>
          </div>
        </form>
      </div>
    </>
  );
};
